import React from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { isFunction } from 'lodash'

import styles from './number-input.module.css'

const propTypes = {
    badInputMessage: PropTypes.string,
    defaultValue: PropTypes.number,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    max: PropTypes.number,
    min: PropTypes.number,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onCustomErrorHandler: PropTypes.func,
    onFocus: PropTypes.func,
    onInput: PropTypes.func,
    placeholder: PropTypes.string,
    rangeErrorMessage: PropTypes.string,
    required: PropTypes.bool,
    requiredErrorMessage: PropTypes.string,
    step: PropTypes.number,
    value: PropTypes.number
}

const defaultProps = {
    disabled: false,
    min: 0,
    step: 1
}
/**
 *
 * @param {Object} props
 * @property {string} [props.className] - CSS selector class names
 * @property {number | string} [props.defaultValue] - Initial input value (for uncontrolled components)
 * @property {boolean} [props.disabled=false] - Flag to disable input
 * @property {string} [props.id] - Input id
 * @property {number} [props.max] - Max input value
 * @property {number} [props.min=0] - Min input value
 * @property {string} [props.name] - Input name
 * @property {func} [props.onBlur] - The event is triggered if the focuse is lost.
 * @property {func} [props.onChange] - The event is triggered if the value changed.
 * @property {func} [props.onCustomErrorHandler] - The event is triggered if the value is invalid.
 * @property {func} [props.onFocus] - The event is triggered if the input has the focus
 * @property {func} [props.onInput] - The event is triggered if the value changed. (Fired after each change.)
 * @property {string} [props.placeholder] - Input placeholder text
 * @property {string} [props.rangeErrorMessage] - Custom error message for range errors.
 * @property {boolean} [props.required] - Flag if input is mandatory
 * @property {string} [props.requiredErrorMessage] - Custom error message for required errors.
 * @property {number} [props.step=1] - Amount for raising the input by using the arrow keys (up/down)
 * @property {number | string} [props.value] - initial input value (for controlled components)
 */
function NumberInput(props) {
    const {
        className,
        defaultValue,
        disabled,
        id,
        max,
        min,
        name,
        onBlur,
        onChange,
        onFocus,
        placeholder,
        required,
        step,
        value
    } = props

    const onMouseOver = event => {
        event.stopPropagation()
        event.preventDefault()
        const numberInput = event.target
        numberInput.setCustomValidity('')
        numberInput.checkValidity()
    }

    const onInput = event => {
        event.stopPropagation()
        event.preventDefault()

        const { onInput } = props
        const numberInput = event.target

        numberInput.setCustomValidity('')
        numberInput.checkValidity()

        if (isFunction(onInput)) {
            onInput(event)
        }
    }

    const onInvalid = event => {
        const { badInputMessage, onCustomErrorHandler, rangeErrorMessage, requiredErrorMessage } = props
        const numberInput = event.target
        const { badInput, rangeOverflow, rangeUnderflow, valueMissing } = numberInput.validity
        const defaultErrorMessage = numberInput.validationMessage

        if (isFunction(onCustomErrorHandler)) {
            numberInput.setCustomValidity('')
            onCustomErrorHandler(event)
        } else {
            if (badInput) {
                numberInput.setCustomValidity(badInputMessage || defaultErrorMessage)
            } else if (rangeOverflow || rangeUnderflow) {
                numberInput.setCustomValidity(rangeErrorMessage || defaultErrorMessage)
            } else if (valueMissing) {
                numberInput.setCustomValidity(requiredErrorMessage || defaultErrorMessage)
            }
        }
    }

    const inputClass = cx(
        styles['number-input'],
        styles['hide-spinner'],
        styles['number-input-right'],
        className,
        't3sel-number-input'
    )

    return (
        <input
            type="number"
            className={inputClass}
            {...{
                defaultValue,
                disabled,
                id,
                max,
                min,
                name,
                onBlur,
                onChange,
                onFocus,
                onInput,
                onInvalid,
                onMouseOver,
                placeholder,
                required,
                step,
                value
            }}
        />
    )
}

NumberInput.propTypes = propTypes
NumberInput.defaultProps = defaultProps

export { NumberInput }
